import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-primary-foreground hover:shadow-glow-primary transition duration-300 border border-primary/30 hover:scale-105",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md hover:shadow-lg transition duration-300",
        outline:
          "border-2 border-primary bg-transparent hover:bg-primary hover:text-primary-foreground hover:shadow-glow-primary transition duration-300",
        secondary:
          "bg-gradient-secondary text-secondary-foreground hover:shadow-glow-secondary transition duration-300 border border-secondary/30 hover:scale-105",
        ghost: "hover:bg-accent/50 hover:text-accent-foreground transition duration-300",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary-glow transition-colors duration-300",
        premium: "bg-gradient-neon text-white hover:shadow-glow-neon bg-[length:200%_100%] hover:bg-[position:100%_0] transition duration-500 hover:scale-105 border border-primary/30",
        glass: "bg-card/30 text-foreground backdrop-blur-md border-2 border-border/50 hover:bg-card/50 hover:border-primary/50 shadow-lg hover:shadow-glow-primary transition duration-300 hover:scale-105",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
